import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation, useQuery } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  SubmitButton,
  TextField,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  ChangePlayerCityMutation,
  ChangePlayerCityMutationVariables,
  PlayerCityFormQuery,
} from './__generated__/PlayerCityForm';

const query = graphql`
  query SanityPlayerCityForm {
    sanityPlayerCityForm {
      title {
        ...SanityLocaleString
      }
      city {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const playerQuery = gql`
  query PlayerCityForm($playerId: ID!) {
    player(playerId: $playerId) {
      id
      city
      countryCode
      street
      zipCode
    }
  }
`;

const playerMutation = gql`
  mutation ChangePlayerCity(
    $playerId: ID!
    $city: String!
    $countryCode: String!
    $street: String!
    $zipCode: String!
  ) {
    changeAddress(
      playerId: $playerId
      city: $city
      countryCode: $countryCode
      street: $street
      zipCode: $zipCode
    ) {
      id
      city
      countryCode
      street
      zipCode
    }
  }
`;

const PlayerCityForm: FC<{ playerId: string }> = ({ playerId }) => {
  const staticData = useStaticQuery<Queries.SanityPlayerCityFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerCityForm;

  const [{ data }] = useQuery<PlayerCityFormQuery>({
    query: playerQuery,
    variables: { playerId },
  });

  const [updatePlayerState, updatePlayer] = useMutation<
    ChangePlayerCityMutation,
    ChangePlayerCityMutationVariables
  >(playerMutation);

  const defaultValues = {
    city: data?.player?.city,
  };

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onSubmit = (values: typeof defaultValues) => {
    if (!data?.player?.id || !values.city) {
      return;
    }

    updatePlayer({
      playerId: data?.player.id,
      street: data.player.street ?? '',
      city: values.city,
      countryCode: data.player.countryCode ?? '',
      zipCode: data.player.zipCode ?? '',
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  if (!form || !data) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <TextField
            title={t(form.city)}
            name="city"
            id="PlayerCityForm__city"
            className="col-span-full"
          />

          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value={t(form.submit)}
            disabled={updatePlayerState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerCityForm;
